"use client";

import type { CSSProperties, ReactNode } from "react";
import {
	BEGINNER_TUTORIAL_STEPS,
	tutorialText,
	type BeginnerTutorialStep,
} from "@/domain/tutorial/BeginnerTutorial";

const glass: CSSProperties = {
	background: "color-mix(in srgb, var(--ds-surface) 94%, transparent)",
	backdropFilter: "blur(24px) saturate(150%)",
	WebkitBackdropFilter: "blur(24px) saturate(150%)",
	border: "1px solid color-mix(in srgb, var(--ds-accent) 40%, var(--ds-divider))",
	boxShadow: "0 18px 48px rgb(0 0 0 / 0.28)",
};

export function TutorialCoachmark({
	step,
	text,
	position = "top",
}: Readonly<{
	step: BeginnerTutorialStep;
	text: string;
	position?: "top" | "bottom";
}>) {
	const index = BEGINNER_TUTORIAL_STEPS.indexOf(step);
	const progress = Math.max(0, Math.min(1, index / (BEGINNER_TUTORIAL_STEPS.length - 1)));
	return (
		<aside
			aria-live="polite"
			style={{
				position: "fixed",
				zIndex: 1000,
				left: "50%",
				transform: "translateX(-50%)",
				width: "min(520px, calc(100vw - 32px))",
				...(position === "top"
					? { top: "calc(env(safe-area-inset-top, 0px) + 72px)" }
					: { bottom: "calc(env(safe-area-inset-bottom, 0px) + 92px)" }),
				borderRadius: "20px",
				padding: "14px 16px",
				...glass,
			}}
		>
			<div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
				<span aria-hidden="true" style={{ color: "var(--ds-accent)", fontSize: "20px" }}>✦</span>
				<strong style={{ flex: 1, color: "var(--ds-text-primary)", lineHeight: 1.45 }}>{text}</strong>
			</div>
			<div
				style={{
					height: "3px",
					marginTop: "10px",
					borderRadius: "999px",
					background: "var(--ds-divider)",
					overflow: "hidden",
				}}
			>
				<div style={{ width: `${progress * 100}%`, height: "100%", background: "var(--ds-accent)" }} />
			</div>
		</aside>
	);
}
export function TutorialModal({
	title,
	text,
	actionLabel,
	onAction,
	onCancel,
	icon = "✦",
}: Readonly<{
	title: string;
	text: string;
	actionLabel: string;
	onAction: () => void;
	onCancel?: () => void;
	icon?: ReactNode;
}>) {
	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label={title}
			style={{
				position: "fixed",
				inset: 0,
				zIndex: 1200,
				display: "grid",
				placeItems: "center",
				padding: "24px",
				background: "color-mix(in srgb, var(--ds-background) 72%, transparent)",
			}}
		>
			<div style={{ width: "min(440px, 100%)", padding: "28px", borderRadius: "28px", textAlign: "center", ...glass }}>
				<div
					style={{
						width: "112px",
						height: "112px",
						margin: "0 auto 20px",
						borderRadius: "50%",
						display: "grid",
						placeItems: "center",
						fontSize: "42px",
						color: "var(--ds-accent)",
						background: "color-mix(in srgb, var(--ds-accent) 16%, transparent)",
						animation: "tutorial-pulse 1.6s ease-in-out infinite alternate",
					}}
				>
					{icon}
				</div>
				<h2 style={{ margin: 0, color: "var(--ds-text-primary)" }}>{title}</h2>
				<p style={{ color: "var(--ds-text-secondary)", lineHeight: 1.6 }}>{text}</p>
				<div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
					{onCancel ? (
						<button type="button" className="ds-button ds-button--secondary" style={{ flex: 1 }} onClick={onCancel}>
							{tutorialText("取消", "取消", "Cancel")}
						</button>
					) : null}
					<button type="button" className="ds-button ds-button--primary" style={{ flex: 1 }} onClick={onAction}>
						{actionLabel}
					</button>
				</div>
			</div>
		</div>
	);
}

export function TutorialDimmer() {
	return (
		<div
			aria-hidden="true"
			style={{
				position: "fixed",
				inset: 0,
				zIndex: 800,
				pointerEvents: "none",
				background: "color-mix(in srgb, var(--ds-background) 16%, transparent)",
			}}
		/>
	);
}
